/**
 * Platform-admin console layout — AdminAuthGate (web-only console at /admin).
 *
 * Mirrors the root app/_layout.tsx AuthGate pattern, but drives off the SEPARATE useAdminSession
 * store (never the tenant useSession) and only ever redirects WITHIN the admin group. The tenant
 * gate explicitly ignores segments[0] === 'admin', so the two gates never fight.
 *
 *  - status 'loading'        → render Slot, no redirect (restore in flight).
 *  - status 'authenticated'  → if on login/verify-mfa/enroll, replace to /admin.
 *  - status 'unauthenticated':
 *      stage 'mfa_required'    → /admin/verify-mfa
 *      stage 'enroll_required' → /admin/enroll
 *      else (and not already on login) → /admin/login
 */
import { useEffect } from 'react';
import { Slot, useRouter, useSegments, useRootNavigationState } from 'expo-router';
import { useAdminSession } from '../../src/auth/adminSession';

function AdminAuthGate() {
  const status = useAdminSession((s) => s.status);
  const stage = useAdminSession((s) => s.stage);
  const router = useRouter();
  const segments = useSegments();
  // Gate redirects on the root navigator being mounted (same reason as the tenant AuthGate).
  const navState = useRootNavigationState();

  useEffect(() => {
    if (!navState?.key) return;
    if (status === 'loading') return;
    if (segments[0] !== 'admin') return; // only act inside the admin group

    const sub = (segments as string[])[1]; // e.g. 'login' | 'verify-mfa' | 'enroll' | 'orgs' | 'audit' | undefined
    const onLogin = sub === 'login';
    const onAuthScreen = onLogin || sub === 'verify-mfa' || sub === 'enroll';

    if (status === 'authenticated') {
      if (onAuthScreen) router.replace('/admin');
      return;
    }

    // unauthenticated
    if (stage === 'mfa_required') {
      if (sub !== 'verify-mfa') router.replace('/admin/verify-mfa');
    } else if (stage === 'enroll_required') {
      if (sub !== 'enroll') router.replace('/admin/enroll');
    } else if (!onLogin) {
      router.replace('/admin/login');
    }
  }, [status, stage, segments, router, navState?.key]);

  // Always render the navigator on first paint (do not return null before <Slot/>).
  return <Slot />;
}

export default function AdminLayout() {
  const restore = useAdminSession((s) => s.restore);

  useEffect(() => {
    void restore();
  }, [restore]);

  return <AdminAuthGate />;
}
